import React from 'react';
import { FormOption } from '../../types/api';
import MomentExpression from '../MomentExpression';

interface QuestionOptionsProps {
  optionType: string;
  options?: FormOption[];
  questionId: string | number;
  moments?: {
    id: number;
    name: string;
    operation: string;
  }[];
  // Evaluation mode props (if provided, we're in evaluation mode; otherwise preview mode)
  isOptionSelected?: (option: FormOption) => boolean;
  onOptionChange?: (optionId: number) => void;
  onScoreChange?: (optionId: number, score: string) => void;
  getOptionScore?: (option: FormOption) => number;
  getRatingIndicator?: (option: FormOption) => React.ReactElement | null;
}

const QuestionOptions: React.FC<QuestionOptionsProps> = ({
  optionType,
  options = [],
  questionId,
  moments = [],
  isOptionSelected,
  onOptionChange,
  onScoreChange,
  getOptionScore,
  getRatingIndicator
}) => {
  // Infer preview mode: if evaluation props are not provided, we're in preview mode
  const isPreview = !isOptionSelected && !onOptionChange;
  if (!options || options.length === 0) return null;

  switch(optionType) {
    case 'YesNo':
    case 'YES_NO':
    case 'MULTICHOICE':
    case 'MULTI_CHOICE':
      return (
        <div className="question-options">
          {options.map((option) => (
            <div key={option.id} className="option-item">
              <div className="option-main">
                {isPreview ? (
                  // Preview mode: non-editable selectors
                  <div className="option-selector">
                    {(optionType === 'MULTICHOICE' || optionType === 'MULTI_CHOICE') ? (
                      <input
                        type="checkbox"
                        disabled
                        readOnly
                        style={{ cursor: 'default', opacity: 0.6 }}
                      />
                    ) : (
                      <input
                        type="radio"
                        name={`question_${questionId}_preview`}
                        disabled
                        readOnly
                        style={{ cursor: 'default', opacity: 0.6 }}
                      />
                    )}
                  </div>
                ) : (
                  // Evaluation mode: editable selectors
                  <div className="option-selector">
                    {(optionType === 'MULTICHOICE' || optionType === 'MULTI_CHOICE') ? (
                      <input
                        type="checkbox"
                        checked={isOptionSelected?.(option) || false}
                        onChange={() => onOptionChange?.(option.id)}
                      />
                    ) : (
                      <input
                        type="radio"
                        name={`question_${questionId}`}
                        checked={isOptionSelected?.(option) || false}
                        onChange={() => onOptionChange?.(option.id)}
                      />
                    )}
                  </div>
                )}
                <div className="option-content">
                  <div className="option-text">{option.description}</div>
                  {option.rated_remarks && (
                    <div className="option-remarks">{option.rated_remarks}</div>
                  )}
                </div>
                {getRatingIndicator && getRatingIndicator(option)}
                <div className="option-score">
                  {isPreview ? (option.points || 0) : (getOptionScore ? getOptionScore(option) : 0)}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
      
    case 'GRADING':
      return (
        <div className="question-options grading-options">
          {options.map((option) => (
            <div key={option.id} className="option-item">
              <div className="option-main">
                {isPreview && (
                  <div className="option-selector">
                    <input
                      type="radio"
                      name={`question_${questionId}_grading_preview`}
                      disabled
                      readOnly
                      style={{ cursor: 'default', opacity: 0.6 }}
                    />
                  </div>
                )}
                <div className="option-content">
                  <div className="option-text">{option.description}</div>
                  {getRatingIndicator && getRatingIndicator(option)}
                  {option.rated_remarks && (
                    <div className="option-remarks">{option.rated_remarks}</div>
                  )}
                </div>
                <div className={isPreview ? "option-score" : "option-score-input"}>
                  {isPreview ? (
                    <span>{option.points || 0}</span>
                  ) : (
                    <>
                      <input
                        type="number"
                        className="score-input"
                        value={getOptionScore ? getOptionScore(option) : 0}
                        onChange={(e) => onScoreChange?.(option.id, e.target.value)}
                        min="0"
                        max={option.points}
                      />
                      <span className="max-points">/ {option.points}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
      
    case 'MOMENT':
      return (
        <div className="question-options moment-options">
          <div className="moment-expression">
            {moments && moments.length > 0 ? (
              <MomentExpression 
                moments={moments}
                isPreview={isPreview}
                onMomentClick={undefined}
              />
            ) : (
              <div className="no-moments">No moments defined</div>
            )}
          </div>
        </div>
      );
      
    default:
      return (
        <div className="question-options">
          {options.map((option) => (
            <div key={option.id} className="option-item">
              <div className="option-main">
                {!isPreview && (
                  <div className="option-selector">
                    <input
                      type="radio"
                      name={`question_${questionId}`}
                      checked={isOptionSelected?.(option) || false}
                      onChange={() => onOptionChange?.(option.id)}
                    />
                  </div>
                )}
                <div className="option-content">
                  <div className="option-text">{option.description}</div>
                  {getRatingIndicator && getRatingIndicator(option)}
                  {option.rated_remarks && (
                    <div className="option-remarks">{option.rated_remarks}</div>
                  )}
                </div>
                {!isPreview && (
                  <div className="option-score">
                    {getOptionScore ? getOptionScore(option) : 0}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
  }
};

export default QuestionOptions;

